<template>
  <footer class="site-footer">
    <div class="container">
      <div class="footer-grid">
        <!-- Location -->
        <div class="footer-col">
          <h5 class="footer-heading">Location</h5>
          <p class="footer-text">
            If you want to contact us about any issue our support available to help you.
          </p>
          <ul class="footer-contact-list">
            <li>
              <i class="bi bi-geo-alt-fill"></i>
              <span>GAUR Clinic, 130-A, Patparganj Village, Delhi - 110091</span>
            </li>
            <li>
              <i class="bi bi-telephone-fill"></i>
              <span>
                <a href="tel:+919811271916">+91-98112-71916</a> (for General Queries),
                <a href="tel:+918454094444">+91-84540-94444</a> (for Online Courses)
              </span>
            </li>
            <li>
              <i class="bi bi-envelope-fill"></i>
              <span>for General Queries -
                <a href="mailto:sngaur9@gmail.com">sngaur9@gmail.com</a>
              </span>
            </li>
            <li>
              <i class="bi bi-envelope-fill"></i>
              <span>for Online Courses -
                <a href="mailto:support@chestcourses.org">support@chestcourses.org</a>
              </span>
            </li>
          </ul>
        </div>

        <!-- Quick Link -->
        <div class="footer-col">
          <h5 class="footer-heading">Quick Link</h5>
          <ul class="footer-links-list">
            <li>
              <router-link to="/">Home</router-link>
            </li>
            <li>
              <router-link to="/about">About Us</router-link>
            </li>
          
            <li>
              <a href="#" @click.prevent="$emit('open-contact')">Contact Us</a>
            </li>
          </ul>
        </div>

        <!-- Event Gallery -->
        <div class="footer-col">
          <h5 class="footer-heading">Event Gallery</h5>
          <div class="footer-gallery-grid">
            <div
              class="gallery-thumb"
              v-for="photo in galleryPhotos"
              :key="photo._id"
              @click="goToPhotoEvent(photo)"
              :title="photo.title || 'View event'"
            >
              <img :src="photo.url" :alt="photo.title || 'Event photo'" loading="lazy" />
            </div>
            <div
              class="gallery-thumb placeholder"
              v-for="n in placeholderCount"
              :key="'placeholder-' + n"
            >
              <i class="bi bi-image"></i>
            </div>
          </div>
        </div>

        <!-- Follow Us + Newsletter -->
        <div class="footer-col">
          <h5 class="footer-heading">Follow Us</h5>
          <div class="d-flex gap-3 mb-4 footer-social">
            <a href="#" class="social-icon text-decoration-none">
              <i class="bi bi-facebook"></i>
            </a>
            <a href="#" class="social-icon text-decoration-none">
              <i class="bi bi-instagram"></i>
            </a>
            <a href="#" class="social-icon text-decoration-none">
              <i class="bi bi-linkedin"></i>
            </a>
            <a href="#" class="social-icon text-decoration-none">
              <i class="bi bi-twitter-x"></i>
            </a>
          </div>

          <h5 class="footer-heading">Signup for Newsletter</h5>
          <form class="newsletter-form" @submit.prevent="subscribeNewsletter">
            <input
              type="email"
              v-model="newsletterEmail"
              placeholder="Enter your email"
              class="form-control newsletter-input"
              required
            />
            <button
              type="submit"
              class="btn btn-primary newsletter-btn"
              :disabled="isSubscribing"
            >
              {{ isSubscribing ? "Submitting..." : "Submit" }}
            </button>
          </form>
          <p
            v-if="newsletterMessage"
            class="newsletter-message"
            :class="newsletterError ? 'text-danger' : 'text-success'"
          >
            {{ newsletterMessage }}
          </p>
        </div>
      </div>

      <hr class="footer-divider" />

      <div class="footer-bottom text-center">
        <p class="mb-0">
          &copy; {{ currentYear }} National College of Chest Physicians (NCCP). All rights reserved.
        </p>
      </div>
    </div>
  </footer>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "../utils/apis.js";

export default {
  name: "NewsletterFooter",
  emits: ["open-contact"],
  data() {
    return {
      galleryPhotos: [],
      newsletterEmail: "",
      newsletterMessage: "",
      newsletterError: false,
      isSubscribing: false,
      currentYear: new Date().getFullYear(),
    };
  },
  computed: {
    placeholderCount() {
      return Math.max(0, 6 - this.galleryPhotos.length);
    },
  },
  async mounted() {
    await this.fetchGalleryPhotos();
  },
  methods: {
    async fetchGalleryPhotos() {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/photos`);
        const photos = Array.isArray(response.data) ? response.data : [];
        this.galleryPhotos = photos.slice(0, 6).map((photo) => ({
          _id: photo._id,
          title: photo.title,
          eventId:
            photo.event && typeof photo.event === "object"
              ? photo.event._id
              : photo.event,
          url:
            photo.imageUrl && photo.imageUrl.startsWith("http")
              ? photo.imageUrl
              : `${API_BASE_URL}${photo.imageUrl}`,
        }));
      } catch (error) {
        console.error("Error fetching gallery photos for footer:", error);
        this.galleryPhotos = [];
      }
    },
    goToPhotoEvent(photo) {
      if (!photo.eventId) return;
      this.$router.push({ name: "NapconEventDetail", params: { id: photo.eventId } });
    },
    async subscribeNewsletter() {
      if (!this.newsletterEmail) return;
      this.isSubscribing = true;
      this.newsletterMessage = "";
      try {
        await axios.post(`${API_BASE_URL}/news/postNesletter`, {
          email: this.newsletterEmail,
        });
        this.newsletterError = false;
        this.newsletterMessage = "Subscribed successfully!";
        this.newsletterEmail = "";
      } catch (error) {
        this.newsletterError = true;
        this.newsletterMessage =
          (error.response && error.response.data && error.response.data.message) ||
          "Something went wrong. Please try again.";
      } finally {
        this.isSubscribing = false;
      }
    },
  },
};
</script>

<style scoped>
.site-footer {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 50px 0 20px;
  margin-top: 20px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
}

.footer-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
}

.footer-heading {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 18px;
  position: relative;
  padding-bottom: 10px;
}

.footer-heading::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 40px;
  height: 2px;
  background: #0d6efd;
}

.footer-text {
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.6;
  margin-bottom: 16px;
}

.footer-contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-contact-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.5;
}

.footer-contact-list li i {
  color: #0d6efd;
  margin-top: 3px;
  flex-shrink: 0;
}

.footer-contact-list a {
  color: #495057;
  text-decoration: none;
}

.footer-contact-list a:hover {
  color: #0d6efd;
  text-decoration: underline;
}

.footer-links-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links-list li {
  margin-bottom: 12px;
}

.footer-links-list a {
  color: #495057;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.footer-links-list a:hover {
  color: #0d6efd;
  padding-left: 4px;
}

.footer-gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.gallery-thumb {
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  overflow: hidden;
  background: #dfe4ea;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-thumb:not(.placeholder):hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.gallery-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-thumb.placeholder {
  cursor: default;
}

.gallery-thumb.placeholder i {
  color: #adb5bd;
  font-size: 1.4rem;
}

.footer-social .social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  color: #2c3e50;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.footer-social .social-icon:hover {
  transform: translateY(-3px);
  color: #0d6efd;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.newsletter-form {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.newsletter-input {
  flex: 1;
  border: 2px solid #e9ecef;
  font-size: 0.9rem;
}

.newsletter-input:focus {
  border-color: #0d6efd;
  box-shadow: 0 2px 15px rgba(13, 110, 253, 0.2);
}

.newsletter-btn {
  font-weight: 600;
  white-space: nowrap;
}

.newsletter-message {
  font-size: 0.85rem;
  margin: 0;
}

.footer-divider {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin: 30px 0 16px;
}

.footer-bottom p {
  font-size: 0.85rem;
  color: #6c757d;
}

@media (max-width: 992px) {
  .footer-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .footer-grid {
    grid-template-columns: 1fr;
  }
}
</style>